import {Injectable} from '@angular/core';
import {CameraService} from './camera.service';
import {SceneUtilsService} from './scene-utils.service';
import {Subject} from 'rxjs/Subject';
import {OrbitService} from './orbit.service';
import {CameraConstraintService} from './camera-constraint.service';
import {GroundConstraint, RoofConstraint} from '../classes/orbitControlConstraint';

declare const THREE: any;

interface ZoomConf {
  min: number;
  max: number;
  step: number;
  default: number;
  current: number;
}

interface RangeMap {
  min: number;
  max: number;
}

abstract class ZoomBase {

  protected config: ZoomConf;
  protected subject: Subject<number>;

  constructor(subject: Subject<number>) {
    this.subject = subject;
  }

  abstract in(e: MouseEvent): void;

  abstract out(e: MouseEvent): void;

  abstract reset(): void;

  abstract set(scale: number, rangeMap: RangeMap);

  setProToolsModConfig(isProTools: boolean) {
    if (isProTools) {
      this.config.max = 10;
    } else {
      this.config.max = 2;
    }
  }

  protected mapIn(value: number, rangeMap: RangeMap): number {
    return this.config.min + ((this.config.max - this.config.min) / (rangeMap.max - rangeMap.min)) * (value - rangeMap.min);
  }

  protected mapOut(rangeMap: RangeMap): number {
    return rangeMap.min + ((rangeMap.max - rangeMap.min) / (this.config.max - this.config.min)) * (this.config.current - this.config.min);
  }

  getCurrentZoom(rangeMap?: RangeMap): number {
    let result = this.config.current;
    if (rangeMap) {
      result = this.mapOut(rangeMap);
    }
    return result;
  }

}

@Injectable()
export class ZoomService {

  on2DScaleChange = new Subject<number>();
  on3DScaleChange = new Subject<number>();

  private readonly zoom2D: ZoomBase;
  private readonly zoom3D: ZoomBase;
  private currentZoomObj: ZoomBase;
  private rangeMap: RangeMap;

  constructor(
    private cameraManager: CameraService,
    private sceneUtils: SceneUtilsService,
    private orbitControl: OrbitService,
    private cameraConstraint: CameraConstraintService
  ) {
    this.rangeMap = {
      min: 1,
      max: 20
    };
    this.zoom2D = new Zoom2d(this.on2DScaleChange, cameraManager, sceneUtils);
    this.zoom3D = new Zoom3d(this.on3DScaleChange, cameraManager, orbitControl, cameraConstraint);
    
    // Initialize with 2D zoom by default
    this.currentZoomObj = this.zoom2D;
    
    this.cameraManager.cameraTrackerSubj.subscribe(isIn3D => this.currentZoomObj = isIn3D ? this.zoom3D : this.zoom2D);
  }

  setProToolsModConfig(isProTools) {
    this.currentZoomObj.setProToolsModConfig(isProTools);
  }

  setRange(rangeMap: RangeMap) {
    this.rangeMap = rangeMap;
  }

  in(e: MouseEvent) {
    if (this.currentZoomObj) {
      this.currentZoomObj.in(e);
    } else {
      console.warn('ZoomService: currentZoomObj is undefined');
    }
  }

  out(e: MouseEvent) {
    if (this.currentZoomObj) {
      this.currentZoomObj.out(e);
    } else {
      console.warn('ZoomService: currentZoomObj is undefined');
    }
  }

  set(value: number) {
    if (this.currentZoomObj) {
      this.currentZoomObj.set(value, this.rangeMap);
    } else {
      console.warn('ZoomService: currentZoomObj is undefined');
    }
  }

  reset() {
    if (this.currentZoomObj) {
      this.currentZoomObj.reset();
    } else {
      console.warn('ZoomService: currentZoomObj is undefined');
    }
  }

  getCurrentZoom() {
    return this.currentZoomObj && this.currentZoomObj.getCurrentZoom(this.rangeMap);
  }

}

class Zoom3d extends ZoomBase {

  config: ZoomConf;

  private minY = 30;
  private cameraDirection = new THREE.Vector3();
  private position = new THREE.Vector3();

  constructor(
    subject: Subject<number>,
    private cameraManager: CameraService,
    private orbitControl: OrbitService,
    private cameraConstraint: CameraConstraintService
  ) {
    super(subject);
    const current = this.getDistanceToCamera();
    this.config = {
      current,
      default: current,
      max: orbitControl.maxDistance,
      min: orbitControl.minDistance,
      step: 50
    };
    cameraConstraint.addConstraint(new GroundConstraint(this.minY));
    cameraConstraint.addConstraint(new RoofConstraint(this.config.max));
    orbitControl.onRadiusChange.subscribe(e => {
      this.config.current = e.radius;
      this.subject.next(this.config.current);
    });
  }

  in(): void {
  }

  out(): void {
  }

  private getDistanceToCamera() {
    return this.cameraManager.getPerspective().position.length();
  }

  private change(delta) {
    const camera = this.cameraManager.getCamera();
    const offset = camera.getWorldDirection(this.cameraDirection).multiplyScalar(delta);
    const newPosition = this.position.copy(camera.position).add(offset);
    const distance = newPosition.length();
    if (newPosition.y >= this.minY && distance >= this.config.min && distance <= this.config.max) {
      camera.position.copy(newPosition);
      this.config.current = distance;
      this.subject.next(this.config.current);
    }
  }

  reset(): void {
    this.config.current = this.config.default;
    this.orbitControl.reset();
    this.subject.next(this.config.current);
  }

  set(scale: number, rangeMap: RangeMap) {
    const value = this.mapIn(scale, rangeMap);
    this.change(this.config.current - value);
  }

}

class Zoom2d extends ZoomBase {

  config: ZoomConf = {
    min: 0.1,
    max: 2,
    step: 0.1,
    default: 1,
    current: 1
  };

  constructor(
    subject: Subject<number>,
    private cameraManager: CameraService,
    private sceneUtils: SceneUtilsService
  ) {
    super(subject);
  }

  in(e: MouseEvent): void {
    const camera = this.cameraManager.camera;
    const current = camera.scale.x;
    const zoom = current - this.config.step;
    this.zoom(zoom, e);
  }

  out(e: MouseEvent): void {
    const camera = this.cameraManager.camera;
    const current = camera.scale.x;
    const zoom = current + this.config.step;
    this.zoom(zoom, e);
  }

  private zoom(value: number, e: MouseEvent) {

    if (value >= this.config.min && value <= this.config.max) {

      const camera = this.cameraManager.camera;
      const point = this.sceneUtils.screenToWorld(e.offsetX, e.offsetY);
      const zoomK = value / this.config.current;
      const matrix = new THREE.Matrix4().makeTranslation(point.x, 0, point.z);
      const scale = new THREE.Matrix4().makeScale(zoomK, 1, zoomK);
      const translate = new THREE.Matrix4().makeTranslation(-point.x, 0, -point.z);

      this.config.current = value;

      matrix.multiply(scale).multiply(translate);

      camera.applyMatrix(matrix);
      camera.updateMatrixWorld();
      camera.updateProjectionMatrix();

      this.subject.next(this.config.current);
    }
  }

  reset(): void {
    this.config.current = this.config.default;
    this.cameraManager.camera.scale.set(1, 1, 1);
    this.subject.next(this.config.current);
  }

  set(scale: number, rangeMap: RangeMap) {
    const value = this.mapIn(scale, rangeMap);
    this.cameraManager.camera.scale.set(value, value, 1);
    this.config.current = value;
    this.subject.next(this.config.current);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/zoom.service.ts